import React from 'react';
import { createClient } from 'redis';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { GetStaticPaths, GetStaticProps } from 'next';

import { getPosts, getUser, getUsers } from '../../lib/graphqlQuery';
import { SiteHead } from '../../components/document/Head/Head';
import FooterBanner from '../../components/Banners/FooterBanner/FooterBanner';
import ArticlePosts from '../../components/ArticlePosts/ArticlePosts';

import { formatUrl } from '../../lib/formatUrl';
import { log_info, log_error } from '../../utils/logger';

import styles from '../../styles/pages/AuthorPage.module.scss';
import getConfig from 'next/config';
import { REDIS_TTL, REDIS_TTL_ERROR } from '../../lib/constant';

const { REDIS_HOST, REDIS_URL } = getConfig().serverRuntimeConfig;

const AuthorPage = (props) => {
  const router = useRouter();
  const { posts, author } = props;
  const currentURL = formatUrl(router.basePath, router.asPath);

  return (
    <>
      <SiteHead
        author={author}
        title={author ? (author.seo && author.seo.title ? author.seo.title : author.name) : ''}
        url={currentURL}
      />
      <>
        <div className={styles.authorPage}>
          <div className={styles.authorSection}>
            <div className={styles.authorAvatar}>
              {author.avatar.foundAvatar && (
                <Image
                  src={author.avatar.url}
                  width={120}
                  height={120}
                  loading="lazy"
                  priority={false}
                  alt="Author avatar"
                />
              )}
            </div>
            <div className={styles.authorInfo}>
              <h1 className={styles.authorInfo__name}>{author.name}</h1>
              <div className={styles.authorInfo__description}>{author.description}</div>
            </div>
          </div>
          <div className={styles.postsSection}>
            <ArticlePosts posts={posts} authorName={author.slug} />
          </div>
        </div>
        <FooterBanner />
      </>
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { params } = context;
  let data;

  if (typeof params.slug === 'string' && params.slug.indexOf(' ') >= 0) {
    return {
      redirect: {
        destination: '/',
        statusCode: 301,
      },
    };
  }

  log_info('getUser', 'param', params.slug);
  if (REDIS_HOST) {
    const redisKey = `author:${params.slug}`;

    const redis = createClient({ url: REDIS_URL });
    redis.on('error', (err) =>
      log_error('Redis Createclient error on geAuthor', 'error', err),
    );

    await redis.connect();
    const value = await redis.get(redisKey);

    if (!value) {
      const { data: dataAuthor } = await getUser(params.slug);
      data = dataAuthor;
      let redisTTL = REDIS_TTL;
      if (data.status && data.status !== 200) {
        redisTTL = REDIS_TTL_ERROR;
        log_error('getAuthor', 'error', data);
      }
      await redis.setEx(redisKey, redisTTL, JSON.stringify(dataAuthor));
      data = dataAuthor;
    } else {
      data = JSON.parse(value);
    }

    await redis.quit();
  } else {
    const { data: dataUser } = await getUser(params.slug);
    data = dataUser;
    log_error('getAuthorContent: Redis option', 'error', 'Redis is not available');
  }
  log_info('getUser', 'response', data);

  log_info('getPosts', 'param', { authorName: params.slug, first: 20 });
  const resPosts = await getPosts({ authorName: params.slug, first: 20 });
  log_info('getPosts', 'response', resPosts);

  return {
    notFound: !data.user,
    props: {
      posts: resPosts.data.posts,
      author: data.user,
    },
    revalidate: 2 * 60, // cache API response for 2 minutes
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const { data } = await getUsers();
  log_info('getUsers', 'response', data);

  return {
    paths:
      data.users && data.users.edges
        ? data.users.edges.map(({ node }) => `/author/${node.slug}`)
        : [],
    fallback: 'blocking',
  };
};

export default AuthorPage;
